/**
 * The tenant's provider record: who the provider is, who validates for them,
 * and the governance rules programme work is held to.
 *
 * Three sections rather than one long form, because they are read by
 * different people at different times.
 *
 * @module components/admin/ProviderSettingsForm
 */

import React, { useState } from "react";

import {
  Button,
  Combobox,
  ErrorAlert,
  FormField,
  Panel,
  SelectField,
  TabCard,
  TabPanel,
  Tabs,
  TextField,
  Toggle,
} from "@/components/ui";
import type { TenantProviderSettings } from "@/server/types";
import type { ProviderContact, ProviderSettings } from "@/types";
import { DEFAULT_LOCALE, DEFAULT_TIME_ZONE, localeOptions, timeZoneOptions } from "@/utils/region";

type ProviderSettingsTab = "identity" | "contacts" | "governance";

/** Why a control is held while the record is being written. */
const SAVING = "Wait for the save in progress to finish.";

export interface ProviderSettingsFormProps {
  /** Null until the first read answers; nothing can be saved before it does. */
  record: TenantProviderSettings | null;
  draft: ProviderSettings;
  setDraft: (update: (current: ProviderSettings) => ProviderSettings) => void;
  onContactChange: (
    contactKey: "validationContact",
    field: keyof ProviderContact,
    value: string,
  ) => void;
  pending: boolean;
  feedback: string;
  onSave: () => void;
  /** Why the record could not be read. The form is not drawn while it is set. */
  loadError?: string;
  onRetry?: () => void;
}

/**
 * @example
 * ```tsx
 * <ProviderSettingsForm record={record} draft={draft} setDraft={setDraft}
 *   onContactChange={updateContact} pending={pending} feedback={feedback} onSave={save} />
 * ```
 */
export function ProviderSettingsForm({
  record,
  draft,
  setDraft,
  onContactChange,
  pending,
  feedback,
  onSave,
  loadError = "",
  onRetry = undefined,
}: ProviderSettingsFormProps): React.ReactElement {
  const [tab, setTab] = useState<ProviderSettingsTab>("identity");
  if (!record && loadError) {
    return (
      <ErrorAlert data-testid="provider-details-error">
        <span className="flex flex-wrap items-center gap-3">
          The provider details could not be loaded. {loadError}
          {onRetry ? (
            <Button size="sm" variant="outline" icon="arrow-clockwise" onClick={onRetry}>
              Try again
            </Button>
          ) : null}
        </span>
      </ErrorAlert>
    );
  }
  let saveRefusal: string | undefined;
  if (!record) {
    saveRefusal = pending
      ? "The provider details are still loading."
      : "The provider details could not be loaded.";
  } else if (pending) {
    saveRefusal = SAVING;
  }
  return (
    <div className="max-w-5xl" data-testid="provider-details-form">
      <Tabs
        variant="lift"
        idPrefix="provider-settings"
        aria-label="Provider settings sections"
        value={tab}
        onChange={(key) => {
          setTab(key as ProviderSettingsTab);
        }}
        items={[
          { key: "identity", label: "Provider identity" },
          { key: "contacts", label: "Contacts" },
          { key: "governance", label: "Governance" },
        ]}
      />

      <TabCard
        data-testid="provider-settings-card"
        footer={
          <div className="flex flex-wrap items-center gap-3">
            <Button
              variant="primary"
              disabled={Boolean(saveRefusal)}
              disabledReason={saveRefusal}
              onClick={onSave}
            >
              Save provider details
            </Button>
            {/* Mounted whatever the outcome: a live region inserted together
            with its text is commonly missed, so a save that failed would
            change the screen in silence. */}
            <span className="text-md text-muted" role="status">
              {feedback}
            </span>
          </div>
        }
      >
        <TabPanel tabKey="identity" idPrefix="provider-settings" active={tab === "identity"}>
          <div className="grid gap-4 md:grid-cols-2">
            {(
              [
                ["name", "Provider name"],
                ["legalName", "Legal name"],
                ["abbreviation", "Abbreviation"],
                ["address", "Provider address"],
                ["website", "Provider website"],
                ["qaProceduresUrl", "Published QA procedures URL"],
                ["contactEmail", "General contact email"],
              ] as const
            ).map(([field, label]) => (
              <FormField
                key={field}
                label={label}
                htmlFor={`provider-${field}`}
                helpText={`Set the ${label.toLowerCase()} used across provider records and exports.`}
              >
                <TextField
                  id={`provider-${field}`}
                  value={String(draft[field] ?? "")}
                  onChange={(value) => setDraft((current) => ({ ...current, [field]: value }))}
                  placeholder={label}
                  readOnly={pending}
                />
              </FormField>
            ))}
            <FormField
              label="Sector"
              htmlFor="provider-sector"
              helpText="Choose the provider sector classification used by templates and reporting."
            >
              <SelectField
                id="provider-sector"
                value={draft.sector ?? ""}
                onChange={(value) =>
                  setDraft((current) => ({
                    ...current,
                    sector:
                      value === "FET" || value === "HE" || value === "FET_HE" ? value : undefined,
                  }))
                }
                options={[
                  { value: "", label: "Not set" },
                  { value: "FET", label: "Further education and training" },
                  { value: "HE", label: "Higher education" },
                  { value: "FET_HE", label: "Further and higher education" },
                ]}
                disabled={pending}
                disabledReason={pending ? SAVING : undefined}
              />
            </FormField>
          </div>
          <Panel
            tone="inset"
            className="mt-6"
            title="Dates and times"
            description="How dates and times appear for everyone in this tenant. Each person can choose their own in Settings."
          >
            <div className="grid gap-4 md:grid-cols-2">
              <FormField
                label="Locale"
                htmlFor="provider-locale"
                helpText="Sets the order and wording of dates, such as 1 Oct 2026."
              >
                <SelectField
                  id="provider-locale"
                  value={draft.locale ?? DEFAULT_LOCALE}
                  onChange={(value) => setDraft((current) => ({ ...current, locale: value }))}
                  options={localeOptions(draft.locale)}
                  disabled={pending}
                  disabledReason={pending ? SAVING : undefined}
                />
              </FormField>
              <FormField
                label="Operating time zone"
                htmlFor="provider-operating-time-zone"
                helpText="The time zone used to show clock times. It does not change which curriculum edition is current."
              >
                <Combobox
                  id="provider-operating-time-zone"
                  value={draft.operatingTimeZone ?? DEFAULT_TIME_ZONE}
                  onChange={(value) =>
                    setDraft((current) => ({ ...current, operatingTimeZone: value }))
                  }
                  options={timeZoneOptions(draft.operatingTimeZone)}
                  icon={null}
                  disabled={pending}
                  disabledReason={pending ? SAVING : undefined}
                />
              </FormField>
            </div>
          </Panel>
        </TabPanel>

        <TabPanel tabKey="contacts" idPrefix="provider-settings" active={tab === "contacts"}>
          <Panel
            tone="inset"
            title="Validation contact"
            description="Contact coordinating validation"
          >
            <div className="grid gap-4 md:grid-cols-2">
              {(
                [
                  ["name", "Name"],
                  ["title", "Job title"],
                  ["email", "Email"],
                  ["phone", "Phone"],
                  ["mobile", "Mobile"],
                  ["address", "Address if different"],
                ] as const
              ).map(([field, label]) => (
                <FormField
                  key={field}
                  label={label}
                  htmlFor={`provider-validationContact-${field}`}
                  helpText={`Set the validation contact ${label.toLowerCase()} used in governance communication.`}
                >
                  <TextField
                    id={`provider-validationContact-${field}`}
                    value={draft.validationContact?.[field] ?? ""}
                    onChange={(value) => onContactChange("validationContact", field, value)}
                    placeholder={label}
                    readOnly={pending}
                  />
                </FormField>
              ))}
            </div>
          </Panel>
        </TabPanel>

        <TabPanel tabKey="governance" idPrefix="provider-settings" active={tab === "governance"}>
          <div className="grid gap-4">
            <FormField
              label="Default revalidation interval"
              htmlFor="provider-revalidation-interval"
              helpText="When an approver leaves the next revalidation date blank, SocratIQ calculates it from the revision's effective date. A manual date always takes precedence."
            >
              <TextField
                id="provider-revalidation-interval"
                type="number"
                min={1}
                max={20}
                step={1}
                suffix="years"
                value={draft.defaultRevalidationIntervalYears ?? ""}
                onChange={(value) =>
                  setDraft((current) => ({
                    ...current,
                    defaultRevalidationIntervalYears: value ? Number(value) : undefined,
                  }))
                }
                readOnly={pending}
                data-testid="provider-revalidation-interval"
              />
            </FormField>
            <Panel tone="inset">
              <div>
                <Toggle
                  id="provider-lead-team-management"
                  label="Allow Programme Leads to manage Programme Teams"
                  description="Enabled by default. Turn off to restrict Programme Team changes to Client Admins only."
                  checked={draft.allowProgrammeLeadTeamManagement !== false}
                  onChange={(checked) => {
                    setDraft((current) => ({
                      ...current,
                      allowProgrammeLeadTeamManagement: checked,
                    }));
                  }}
                  disabled={pending}
                  disabledReason={pending ? SAVING : undefined}
                  data-testid="provider-lead-team-management"
                />
              </div>
            </Panel>
          </div>
        </TabPanel>
      </TabCard>
    </div>
  );
}
